import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Drawer,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  leadsTableStateAtom,
  campaignLeadManagementAtom,
  assignedCampaignUserAtom,
  userInformationAtom,
} from '../../Services/_states';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import DateRangeFilter from '../Shared/DateRangeFilter/DateRangeFilter.tsx';
import { dateFormate, convertToReadableFormat } from '../../utils/helper.jsx';
import { ConstantActions } from '../../Services/_actions/constant.actions.js';
import { fi } from 'date-fns/locale';
import CommonTimePicker from '../Shared/DateTimePicker/TimePicker.tsx';

interface DynamicFilter {
  key_name: string;
  value: any; // boolean | string | string[] | null
  operator: string;
  field_type: string;
  alias_name: string;
  key_options?: (string | number)[];
  statusOption?: string;
  status_id?: string | number;
  sub_status_id?: string | number;
}

const toOptions = (arr: (string | number)[] = []) =>
  arr.map((v) => {
    const str = String(v);
    return { label: convertToReadableFormat(str), value: str };
  });

const getStatusNamesByType = (list: any[] = [], typeKey?: string) => {
  try {
    if (!typeKey) return [];
    return list
      .filter((s: any) => (s?.type_of_status ?? s?.type) === typeKey)
      .map((s: any) => s.status_name)
      .filter(Boolean);
  } catch {
    return [];
  }
};

const getSubStatusNamesByParent = (list: any[] = [], parentKey?: string) => {
  try {
    if (!parentKey) return [];
    const pickSubs = (s: any) =>
      s?.substatuses ?? s?.sub_status ?? s?.subStatus ?? s?.children ?? s?.options ?? [];

    return list
      .filter((s: any) => (s?.type_of_status ?? s?.type) === parentKey)
      .flatMap((s: any) => pickSubs(s))
      .map((x: any) => x?.name ?? x?.label ?? x)
      .filter(Boolean);
  } catch {
    return [];
  }
};

const getOperatorsForFieldType = (fieldType: string) => {
  const base = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' },
  ];
  const textOps = [
    { value: 'contains', label: 'Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
  ];
  const rangeOps = [
    { value: 'gt', label: 'Greater Than' },
    { value: 'gte', label: 'Greater or Equal' },
    { value: 'lt', label: 'Less Than' },
    { value: 'lte', label: 'Less or Equal' },
    { value: 'range', label: 'Between' },
  ];
  const timeRange = [
    { value: 'gt', label: 'Greater Than' },
    { value: 'gte', label: 'Greater or Equal' },
    { value: 'lt', label: 'Less Than' },
    { value: 'lte', label: 'Less or Equal' },
  ];

  switch (fieldType) {
    case 'STRING':
    case 'EMAIL':
    case 'TEXT':
    case 'TEXTAREA':
      return [...base, ...textOps];
    case 'INT':
    case 'FLOAT':
      return [
        ...base,
        ...rangeOps,
      ];
    case 'DATE':
    case 'TIME':
      return [...base, ...timeRange];
    case 'BOOLEAN':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
      ];
    case 'CHECKBOX':
    case 'RADIO':
    case 'DROPDOWN':
      return base;
    default:
      return [...base, ...textOps];
  }
};

interface DynamicFiltersProps {
  open: boolean;
  onClose: () => void;
  typeOfStatusList?: Record<string, any>;
}

const DynamicFilters: React.FC<DynamicFiltersProps> = ({
  open,
  onClose,
  typeOfStatusList = {},
}) => {
  const [tableState, setTableState] = useRecoilState<any>(leadsTableStateAtom);
  const campaignLeadManagementData = useRecoilValue<any>(campaignLeadManagementAtom);
  const assignedUsersByCampaign = useRecoilValue(assignedCampaignUserAtom);
  const action = ConstantActions();
  const statusList =
    campaignLeadManagementData?.statusData?.[
    campaignLeadManagementData?.selectedForm?.leads_form_id
    ] ?? [];

  const campaignId = campaignLeadManagementData?.selectedCampaign?.proposal_id;
  const assignedUsersForCampaign = assignedUsersByCampaign?.[campaignId] ?? [];
  const isCampaignOwner = tableState?.isCampaignOwner ?? false;

  const [statusObj, setStatusObj] = useState<any>(tableState?.filters?.statusObj || null);
  const [assigneeObj, setAssigneeObj] = useState<any>(tableState?.filters?.assigneeObj || null);
  const [dateRange, setDateRange] = useState<{ startDate: any; endDate: any }>({
    startDate: tableState?.filters?.start_date ? new Date(tableState.filters.start_date) : null,
    endDate: tableState?.filters?.end_date ? new Date(tableState.filters.end_date) : null,
  });
  const loggedInUser = useRecoilValue(userInformationAtom);
  const orgId = loggedInUser?.profile?.organisation?.organisation_id;
  const [loadingAssignedUsers, setLoadingAssignedUsers] = useState(false);
  const [assignedUsersList, setAssignedUsersList] = useState<any[]>(assignedUsersForCampaign || []);
  const [filtersArray, setFiltersArray] = useState<DynamicFilter[]>([]);
  const assignLead = async () => {
    if (!orgId) return;

    setLoadingAssignedUsers(true);
    try {
      const res = await action.getUserMinimalList(orgId, { campaign_id: campaignId ?? '' });
      const normalized =
        Array.isArray(res) && res.length
          ? res.map((u: any) => ({
            ...u,
            label: u.username || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
            value: u.id ?? u.profile_id ?? null,
          }))
          : [];
      setAssignedUsersList(normalized);
    } catch (err) {
      console.error('getUserMinimalList error', err);
      setAssignedUsersList([]);
    } finally {
      setLoadingAssignedUsers(false);
    }
  };

  useEffect(() => {
    if (open) {
      assignLead();
    }
  }, [open, campaignId, orgId]);

  useEffect(() => {
    const ls: any = JSON.parse(localStorage.getItem('leadWebFormData') || '{}');
    const itemsObj = ls?.webForm?.leads_form_items;

    if (!itemsObj) return;

    const items: any[] = Array.isArray(itemsObj) ? itemsObj : Object.values(itemsObj);

    const initial: DynamicFilter[] = items
      .filter((f: any) => f?.isFilter)
      .map((f: any) => {
        // default value based on type
        const defaultValue = f.key_type === 'BOOLEAN' ? null : f.key_type === 'CHECKBOX' ? [] : '';

        // Start with saved options for non-status types
        let hydratedOptions: (string | number)[] = f.key_options || [];

        // Inject options for Status/Sub Status from statusData (statusList)
        // if (f.statusOption === 'is_status') {
        //   hydratedOptions = getStatusNamesByType(statusList, f.key_name);
        // } else if (f.statusOption === 'is_sub_status') {
        //   hydratedOptions = getSubStatusNamesByParent(statusList, f.status_parent_key);
        // }

        return {
          key_name: f.key_name,
          value: defaultValue,
          // operator: 'equals',
          operator: f.key_type === 'DATE' ? 'range' : 'equals',
          field_type: f.key_type,
          alias_name: f.alias_name,
          key_options: hydratedOptions,
          statusOption: f.statusOption || null,
          status_id: f.status_id || null,
        };
      });

    setFiltersArray(initial);
  }, []);

  useEffect(() => {
    if (!open || !filtersArray.length) return;
    const ls: any = JSON.parse(localStorage.getItem('leadWebFormData') || '{}');
    const itemsObj = ls?.webForm?.leads_form_items || {};
    const allItems: any[] = Array.isArray(itemsObj) ? itemsObj : Object.values(itemsObj);

    setFiltersArray((prev) =>
      prev.map((flt) => {
        const raw = allItems.find((it: any) => it?.key_name === flt.key_name);
        if (!raw) return flt;

        if (raw.statusOption === 'is_status') {
          return {
            ...flt,
            key_options: getStatusNamesByType(statusList, raw.key_name),
          };
        }

        if (raw.statusOption === 'is_sub_status') {
          return {
            ...flt,
            key_options: getSubStatusNamesByParent(statusList, raw.status_parent_key),
          };
        }

        return flt;
      })
    );
  }, [statusList, open, filtersArray.length]);

  const normalizeSavedDynamicFilters = (saved: any[] = []): DynamicFilter[] => {
    return saved.map((s) => ({
      key_name: s?.matched_with?.key_name,
      value: s?.custom_value ?? '',
      operator: s?.operator || 'equals',
      field_type: s?.valueType || 'STRING',
      alias_name: s?.matched_with?.key_name,
      statusOption: s?.statusOption || null,
      status_id: s?.param?.status_id || null,
      sub_status_id: s?.param?.sub_status_id || null,
    }));
  };


  useEffect(() => {
    if (!open) return;
    const filters = tableState?.filters || {};

    setStatusObj(filters.statusObj || null);
    setAssigneeObj(filters.assigneeObj || null);
    setDateRange({
      startDate: filters.start_date ? new Date(filters.start_date) : null,
      endDate: filters.end_date ? new Date(filters.end_date) : null,
    });

    // const savedDynamicFilters: DynamicFilter[] = Array.isArray(filters.dynamicFilters)
    //   ? filters.dynamicFilters
    //   : [];

    const savedDynamicFilters: DynamicFilter[] =
      Array.isArray(filters.dynamicFilters)
        ? normalizeSavedDynamicFilters(filters.dynamicFilters)
        : [];


    setFiltersArray((prev) =>
      prev.map((f) => {
        const saved = savedDynamicFilters.find((s) => s.key_name === f.key_name);
        return saved
          ? {
            ...f,
            value: saved.value,
            operator: saved.operator,
            statusOption: saved.statusOption || f.statusOption,
            status_id: saved.status_id || f.status_id,
            sub_status_id: saved.sub_status_id || f.sub_status_id,
          }
          : f;
      })
    );
  }, [open, tableState.filters]);

  const updateFilter = (index: number, updates: Partial<DynamicFilter>) =>
    setFiltersArray((prev) => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)));

  const isActive = (f: DynamicFilter) =>
    ['is_empty', 'is_not_empty'].includes(f.operator) ||
    (f.field_type === 'BOOLEAN'
      ? f.value === true || f.value === false
      : Array.isArray(f.value)
        ? f.value.length > 0
        : f.value !== '' && f.value !== null && f.value !== undefined);

  const activeDynamicFiltersCount = filtersArray.filter(isActive).length;

  const activeLeadFiltersCount =
    (statusObj ? 1 : 0) +
    (isCampaignOwner && assigneeObj ? 1 : 0) +
    (dateRange.startDate || dateRange.endDate ? 1 : 0);

  const totalActive = activeDynamicFiltersCount + activeLeadFiltersCount;

  // Builders (pure) for single recoil update
  const buildLeadFilters = () => {
    const startDate = dateRange.startDate ? dateFormate(dateRange.startDate, 'YYYY-MM-DD') : null;
    const endDate = dateRange.endDate ? dateFormate(dateRange.endDate, 'YYYY-MM-DD') : null;
    return {
      statusObj: statusObj || null,
      status: statusObj?.status_name || '',
      assigneeObj: isCampaignOwner ? assigneeObj || null : null,
      lead_assigned_to: isCampaignOwner ? (assigneeObj?.id ?? '') : '',
      start_date: startDate,
      end_date: endDate,
    };
  };

  const buildDynamicFilters = () =>
    filtersArray.filter(isActive).map((f) => ({
      key_name: f.key_name,
      value: f.value,
      operator: f.operator,
      statusOption: f.statusOption,
      status_id: f.status_id,
      sub_status_id: f.sub_status_id,
    }));

  const getValueTypeByFieldType = (fieldType: string) => {
    switch (fieldType) {
      case 'INT':
      case 'FLOAT':
      case 'PINCODE':
      case 'NUMBER':
        return 'number';

      case 'BOOLEAN':
        return 'boolean';

      case 'DATE':
        return 'date';
      case 'TIME':
        return 'time';
      default:
        return 'string';
    }
  };

  const transformDynamicFilters = (filters: DynamicFilter[]) => {
    return filters.map((f) => {
      const rule: any = {
        left: 'others',
        operator: f.operator || 'equals',
        valueType: getValueTypeByFieldType(f.field_type),
        caseInsensitive: true,
        trim: true,
        matched_with: {
          key_name: f.key_name,
        },
        matched_field_type: 'dynamic',
      };

      const isStatus = f.statusOption === 'is_status';
      const isSubStatus = f.statusOption === 'is_sub_status';

      // ✅ NORMAL FIELDS
      if (!isStatus && !isSubStatus) {
        rule.custom_value =
          f.value === undefined || f.value === null ? '' : f.value;
      }

      // ✅ STATUS
      if (isStatus) {
        rule.param = {
          status_id: f.status_id || '',
        };
      }

      // ✅ SUB STATUS
      if (isSubStatus) {
        rule.param = {
          sub_status_id: f.sub_status_id || '',
        };
      }

      return rule;
    });
  };

  // DynamicFilters.tsx — handleApplyAll fix

  const handleApplyAll = () => {
    const lead = buildLeadFilters();
    const previous = Array.isArray(tableState?.filters?.dynamicFilters)
      ? tableState.filters.dynamicFilters
      : [];
    const activeFilters = filtersArray.filter(isActive);

    const hasAnyFilter =
      activeFilters.length > 0 ||
      dateRange.startDate ||
      dateRange.endDate ||
      statusObj ||
      assigneeObj;

    if (!hasAnyFilter) {
      onClose({
        ...tableState?.filters,
        dynamicFilters: [],
        start_date: null,
        end_date: null,
        statusObj: null,
        assigneeObj: null,
        status: '',
        lead_assigned_to: '',
        offset: 0,
      });
      return;
    }

    const current = transformDynamicFilters(activeFilters);

    const map = new Map<string, any>();
    previous.forEach((f: any) => {
      const key = f?.matched_with?.key_name;
      if (key) map.set(key, f);
    });
    current.forEach((f: any) => {
      const key = f?.matched_with?.key_name;
      if (key) map.set(key, f);
    });

    const mergedDynamicFilters = Array.from(map.values());

    onClose({
      ...tableState?.filters,
      ...lead,
      dynamicFilters: mergedDynamicFilters,
      start_date: dateRange.startDate
        ? dateFormate(dateRange.startDate, 'YYYY-MM-DD')
        : null,
      end_date: dateRange.endDate
        ? dateFormate(dateRange.endDate, 'YYYY-MM-DD')
        : null,
      offset: 0,
    });
  };

  // const handleApplyAll = () => {
  //   const lead = buildLeadFilters();
  //   const previous = Array.isArray(tableState?.filters?.dynamicFilters)
  //     ? tableState.filters.dynamicFilters
  //     : [];
  //   const activeFilters = filtersArray.filter(isActive);

  //   // ✅ If no active filters → behave like Clear All
  //   if (activeFilters.length === 0) {
  //     onClose({
  //       ...tableState?.filters,
  //       dynamicFilters: [],
  //       start_date: null,
  //       end_date: null,
  //       statusObj: null,
  //       assigneeObj: null,
  //       offset: 0,
  //     });
  //     return;
  //   }


  //   const current = transformDynamicFilters(activeFilters);

  //   const map = new Map<string, any>();

  //   previous.forEach((f: any) => {
  //     const key = f?.matched_with?.key_name;
  //     if (key) map.set(key, f);
  //   });

  //   current.forEach((f: any) => {
  //     const key = f?.matched_with?.key_name;
  //     if (key) map.set(key, f);
  //   });

  //   const mergedDynamicFilters = Array.from(map.values());
  //   const filtersToApply = { ...tableState?.filters, ...lead, dynamicFilters: mergedDynamicFilters, offset: 0, }

  //   onClose({
  //     ...tableState?.filters,
  //     ...lead,
  //     dynamicFilters: mergedDynamicFilters,
  //     start_date: dateRange.startDate ? dateFormate(dateRange.startDate, 'YYYY-MM-DD') : null,
  //     end_date: dateRange.endDate ? dateFormate(dateRange.endDate, 'YYYY-MM-DD') : null,
  //     offset: 0,
  //   });
  // };

  // handleClearAll fix — dateRange state bhi reset karo
  const handleClearAll = () => {
    setFiltersArray((prev) =>
      prev.map((f) => ({
        ...f,
        value: f.field_type === 'BOOLEAN' ? null : f.field_type === 'CHECKBOX' ? [] : '',
        operator: 'equals',
        status_id: '',
        sub_status_id: '',
        statusOption: f.statusOption || null,
      }))
    );

    setDateRange({ startDate: null, endDate: null });
    setStatusObj(null);
    setAssigneeObj(null);

    onClose({
      ...tableState?.filters,
      offset: 0,
      dynamicFilters: [],
      start_date: null,
      end_date: null,
      statusObj: null,
      assigneeObj: null,
      status: '',
      lead_assigned_to: '',
    });
  };

  // const handleClearAll = () => {
  //   setFiltersArray((prev) =>
  //     prev.map((f) => ({
  //       ...f,
  //       value: f.field_type === 'BOOLEAN' ? null : f.field_type === 'CHECKBOX' ? [] : '',
  //       operator: 'equals',
  //       status_id: '',
  //       sub_status_id: '',
  //       statusOption: f.statusOption || null,
  //     }))
  //   );

  //   onClose({
  //     ...tableState?.filters,
  //     offset: 0,
  //     dynamicFilters: [],
  //     start_date: null,
  //     end_date: null,

  //   });
  // };
  // Renderers for dynamic filters
  const renderValueInput = (filter: DynamicFilter, index: number) => {
    const { field_type, value, key_options = [] } = filter;

    switch (field_type) {
      case 'BOOLEAN':
        return (
          <SearchableDropdown
            size="small"
            label="Value"
            options={[
              { label: 'True', value: true },
              { label: 'False', value: false },
            ]}
            value={typeof value === 'boolean' ? { label: value ? 'True' : 'False', value } : null}
            onChange={(opt: any) =>
              updateFilter(index, {
                value: typeof opt?.value === 'boolean' ? opt.value : null,
              })
            }
            labelKey="label"
            valueKey="value"
          />
        );

      case 'CHECKBOX': // multi
        return (
          <SearchableDropdown
            size="small"
            label="Select Options"
            multiSelect
            options={toOptions(key_options)}
            value={(Array.isArray(value) ? value : []).map((v) => {
              const str = String(v);
              return { label: convertToReadableFormat(str), value: str };
            })}
            onChange={(arr: any[]) =>
              updateFilter(index, { value: (arr || []).map((o) => o.value) })
            }
            labelKey="label"
            valueKey="value"
          />
        );

      case 'RADIO':
      case 'DROPDOWN': // single
        if (filter?.statusOption === 'is_status') {
          return (
            <SearchableDropdown
              size="small"
              label="Value"
              options={typeOfStatusList[filter?.key_name] || []}
              value={
                typeOfStatusList[filter?.key_name]?.find(
                  (s: any) => s.status_id === filter?.status_id
                ) || null
              }
              onChange={(opt: any) =>
                updateFilter(index, {
                  value: opt?.status_name || '',
                  status_id: opt?.status_id || '',
                  statusOption: filter?.statusOption || '',
                })
              }
              labelKey="status_name"
              valueKey="status_id"
            />
          );
        } else if (filter?.statusOption === 'is_sub_status') {
          return (
            <SearchableDropdown
              size="small"
              label="Value"
              options={typeOfStatusList[filter?.key_name] || []}
              value={
                typeOfStatusList[filter?.key_name]?.find(
                  (s: any) => s.sub_status_id === filter?.sub_status_id
                ) || null
              }
              onChange={(opt: any) =>
                updateFilter(index, {
                  value: opt?.status_name || '',
                  status_id: opt?.status_id || '',
                  statusOption: filter?.statusOption || '',
                  sub_status_id: opt?.sub_status_id || '',
                })
              }
              labelKey="name"
              valueKey="sub_status_id"
            />
          );
        }
        return (
          <SearchableDropdown
            size="small"
            label="Value"
            options={toOptions(key_options)}
            value={
              typeof value === 'string' && value
                ? { label: convertToReadableFormat(value), value }
                : null
            }
            onChange={(opt: any) => updateFilter(index, { value: opt?.value || '' })}
            labelKey="label"
            valueKey="value"
          />
        );

      case 'DATE':
        if (filter.operator === 'range') {
          return (
            <DateRangeFilter
              buttonLabel='Select Date'
              startDate={value?.start_date ?? null}
              endDate={value?.end_date ?? null}
              onApply={(range: any) => {
                const startStr = range?.startDate ?? null;
                const endStr = range?.endDate ?? null;
                updateFilter(index, { value: { start_date: startStr, end_date: endStr } });
              }}
            />
          );
        } else {
          return (
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Value"
              InputLabelProps={{ shrink: true }}
              value={value || ''}
              onChange={(e) => updateFilter(index, { value: e.target.value })}
            />
          );
        }
      case 'TIME':
        return (
          <CommonTimePicker
            label="Value"
            value={value || null}
            onChange={(time: any) => {
              if (time instanceof Date && !isNaN(time.getTime())) {
                const hh = String(time.getHours()).padStart(2, '0');
                const mm = String(time.getMinutes()).padStart(2, '0');
                updateFilter(index, { value: `${hh}:${mm}` });
              } else {
                updateFilter(index, { value: time });
              }
            }}
            size="small"
            fullWidth
          />
        );

      case 'STRING':
      case 'EMAIL':
      case 'INT':
      case 'FLOAT':
      case 'PHONE':
      case 'PASSWORD':
      case 'TEXT':
      case 'TEXTAREA':
        if (filter.operator === 'range') {
          return (
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="start_number"
                  value={value?.start_number ?? ''}
                  onChange={(e) =>
                    updateFilter(index, {
                      value: {
                        start_number: e.target.value,
                        end_number: value?.end_number ?? ''
                      }
                    })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="end_number"
                  value={value?.end_number ?? ''}
                  onChange={(e) =>
                    updateFilter(index, {
                      value: {
                        start_number: value?.start_number ?? '',
                        end_number: e.target.value
                      }
                    })
                  }
                />
              </Grid>
            </Grid>
          );
        }
      default:
        return (
          <TextField
            fullWidth
            size="small"
            type={
              field_type === 'EMAIL'
                ? 'email'
                : field_type === 'FLOAT' || field_type === 'INT'
                  ? 'number'
                  : field_type === 'PASSWORD'
                    ? 'password'
                    : 'text'
            }
            label="Value"
            value={value ?? ''}
            onChange={(e) => updateFilter(index, { value: e.target.value })}
            multiline={field_type === 'TEXT' || field_type === 'TEXTAREA'}
            minRows={field_type === 'TEXT' || field_type === 'TEXTAREA' ? 2 : undefined}
          />
        );
    }
  };
  const renderDynamicField = (filter: DynamicFilter, index: number) => {
    const operators = getOperatorsForFieldType(filter.field_type);
    const showValue = !['is_empty', 'is_not_empty'].includes(filter.operator);
    const isRangeOperator = filter.operator === 'range';
    const hideOperator = filter.field_type === 'DATE';
    const displayLabel =
      convertToReadableFormat(filter.alias_name) || convertToReadableFormat(filter.key_name);

    return (
      <Grid container spacing={1}>
        <Grid item xs={isRangeOperator ? 3 : 3}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            {displayLabel}
          </Typography>
        </Grid>
        {!hideOperator && (
          <Grid item xs={isRangeOperator ? 3 : 5}>
            <SearchableDropdown
              size="small"
              label="Filter Type"
              options={operators}
              value={operators.find((op) => op.value === filter.operator) || null}
              onChange={(opt: any) => updateFilter(index, { operator: opt?.value || 'equals' })}
              labelKey="label"
              valueKey="value"
            />
          </Grid>
        )}
        {showValue && (
          <Grid item xs={isRangeOperator ? 6 : 4}>
            {renderValueInput(filter, index)}
          </Grid>
        )}
      </Grid>
    );
  };
  const handleDateRangeApply = (range) => {
    setDateRange(range);
  };

  return (
    <Box p={2} minWidth={220} maxWidth={'100%'}>
      {/* <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: 440, zIndex: 1300 } }}
      > */}
      {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}> */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ fontSize: '14px' }}>
          Filters
        </Typography>

        <Box sx={{ pb: "10px", overflowY: 'auto', height: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DateRangeFilter
            onApply={handleDateRangeApply}
            buttonLabel="Create At"
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        </Box>
      </Box>
      <Divider></Divider>

      <Box sx={{ pt: 2, mt: 1, overflowY: 'auto', maxHeight: '28vh' }}>
        {/* {filtersArray.length === 0 ? (
          <Typography variant="body2">No filterable fields available</Typography>
        ) : ( */}
        {filtersArray.map((f, i) => (
          <Box key={`${f.key_name}-${i}`} sx={{ mb: 2 }}>
            {renderDynamicField(f, i)}
          </Box>
        ))}
        {/* )} */}
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 1.5,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
        }}
      >
        <Button variant="outlined" size="small" onClick={handleClearAll}>
          Clear All
        </Button>
        <Button variant="contained" size="small" onClick={handleApplyAll}>
          Apply
        </Button>
      </Box>
      {/* </Drawer> */}
    </Box>
  );
};

export default DynamicFilters;
